// src/components/layout/AppLayout.js
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSectionsForRole } from '../../config/modules';
import * as Icons from 'lucide-react';

export function AppLayout() {
  const { userProfile, signOutUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const sections = getSectionsForRole(userProfile?.role, userProfile?.moduleAccess ?? null);

  // Auto-expand the section whose child matches the current route
  useEffect(() => {
    const active = sections.find(s =>
      s.children.some(m => location.pathname.startsWith(m.path))
    );
    if (active) setExpandedSection(active.id);
  }, [location.pathname]);

  function handleSectionClick(section) {
    if (section.comingSoon) return;
    if (!sidebarOpen) setSidebarOpen(true);
    setExpandedSection(prev => prev === section.id ? null : section.id);
  }

  async function handleSignOut() {
    await signOutUser();
    navigate('/login');
  }

  return (
    <div style={styles.shell}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: sidebarOpen ? 220 : 56 }}>
        {/* Logo */}
        <div style={styles.logo}>
          <span style={styles.logoMark}>⬡</span>
          {sidebarOpen && <span style={styles.logoText}>ROOTS</span>}
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {sections.map(section => {
            const Icon = Icons[section.icon] || Icons.Circle;
            const isExpanded = expandedSection === section.id;
            const hasActiveChild = section.children.some(m =>
              location.pathname.startsWith(m.path)
            );

            return (
              <div key={section.id}>
                {/* Section row */}
                <div
                  onClick={() => handleSectionClick(section)}
                  style={{
                    ...styles.sectionItem,
                    ...(hasActiveChild ? styles.sectionItemActive : {}),
                    ...(section.comingSoon ? styles.sectionItemDisabled : {}),
                    cursor: section.comingSoon ? 'default' : 'pointer',
                  }}
                  title={section.name}
                >
                  <Icon size={17} style={{ flexShrink: 0 }} />
                  {sidebarOpen && (
                    <>
                      <span style={styles.sectionLabel}>{section.name}</span>
                      {section.comingSoon
                        ? <span style={styles.soonBadge}>Soon</span>
                        : <Icons.ChevronRight
                            size={13}
                            style={{
                              ...styles.chevron,
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}
                          />
                      }
                    </>
                  )}
                </div>

                {/* Sub-items */}
                {sidebarOpen && !section.comingSoon && (
                  <div style={{
                    ...styles.children,
                    maxHeight: isExpanded ? '400px' : '0',
                  }}>
                    {section.children.map(page => {
                      const PageIcon = Icons[page.icon] || Icons.Circle;
                      return (
                        <NavLink
                          key={page.slug}
                          to={page.path}
                          style={({ isActive }) => ({
                            ...styles.pageItem,
                            ...(isActive ? styles.pageItemActive : {}),
                          })}
                        >
                          <PageIcon size={15} style={{ flexShrink: 0 }} />
                          <span>{page.name}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div style={styles.userFooter}>
          {sidebarOpen && (
            <div style={styles.userInfo}>
              <div style={styles.userName}>{userProfile?.displayName}</div>
              <div style={styles.userRole}>{userProfile?.role}</div>
            </div>
          )}
          <button onClick={handleSignOut} style={styles.signOutBtn} title="Sign out">
            <Icons.LogOut size={15} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={styles.main}>
        {/* Top bar */}
        <header style={styles.topBar}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={styles.toggleBtn}
          >
            <Icons.Menu size={18} />
          </button>
          <div style={styles.topBarRight}>
            <div style={styles.avatar}>
              {userProfile?.photoURL ? (
                <img src={userProfile.photoURL} alt="" style={styles.avatarImg} />
              ) : (
                <span>{userProfile?.displayName?.[0]}</span>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div style={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

const styles = {
  shell: {
    display: 'flex',
    height: '100vh',
    background: '#f8f7f4',
    fontFamily: "'DM Sans', sans-serif",
  },
  sidebar: {
    background: '#1a1f16',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease',
    overflow: 'hidden',
    flexShrink: 0,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '20px 14px',
    borderBottom: '1px solid #2d3328',
  },
  logoMark: {
    fontSize: 20,
    color: '#7eb86a',
    flexShrink: 0,
  },
  logoText: {
    color: '#f0ede8',
    fontWeight: 700,
    fontSize: 17,
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap',
  },
  nav: {
    flex: 1,
    padding: '10px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  sectionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 8px',
    borderRadius: 7,
    color: '#8a9485',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    transition: 'background 0.15s, color 0.15s',
  },
  sectionItemActive: {
    color: '#c8e6b8',
  },
  sectionItemDisabled: {
    opacity: 0.45,
  },
  sectionLabel: {
    flex: 1,
    overflow: 'hidden',
  },
  chevron: {
    color: '#4a5045',
    flexShrink: 0,
    transition: 'transform 0.18s ease',
  },
  soonBadge: {
    fontSize: 9,
    fontWeight: 700,
    color: '#5a6455',
    background: '#252b21',
    borderRadius: 4,
    padding: '2px 5px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  children: {
    overflow: 'hidden',
    transition: 'max-height 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    paddingLeft: 8,
    marginBottom: 2,
  },
  pageItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 8px 7px 20px',
    borderRadius: 6,
    color: '#6b7566',
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'background 0.12s, color 0.12s',
    borderLeft: '1px solid #2d3328',
  },
  pageItemActive: {
    background: '#2d3328',
    color: '#c8e6b8',
    borderLeftColor: '#7eb86a',
  },
  userFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 10px',
    borderTop: '1px solid #2d3328',
  },
  userInfo: {
    overflow: 'hidden',
  },
  userName: {
    color: '#d4d0c8',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userRole: {
    color: '#6b7566',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: 2,
  },
  signOutBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7566',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    height: 56,
    background: '#fff',
    borderBottom: '1px solid #e8e4de',
  },
  toggleBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7566',
    display: 'flex',
    alignItems: 'center',
    padding: 4,
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#2d3328',
    color: '#c8e6b8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: 24,
  },
};
